import React, { useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_INVOICE_BY_ID,
  GET_TRANSACTION_DETAIL,
  GET_TRANSACTION_IMAGES,
  GetInvoiceByIdData,
  GetInvoiceByIdVars,
  GetTransactionDetailData,
  GetTransactionDetailVars,
  GetTransactionImagesData,
  GetTransactionImagesVars,
} from "common/graphql";
import Paper from "@mui/material/Paper";
import { LocalLevelError } from "@app.automotus.io/components/common/LocalLevelError";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { ImageCarousel } from "@app.automotus.io/components/ImageCarousel";
import type { CarouselSlide } from "@app.automotus.io/components/ImageCarousel";
import { LoadingSkeleton } from "@app.automotus.io/components/LoadingSkeleton";

export const InvoiceSessionImageCarousel: React.FC<InvoiceSessionImageCarouselProps> = ({
  invoiceId,
  transactionId,
}) => {
  const {
    data: invoiceData,
    loading: invoiceLoading,
    error: invoiceError,
    refetch: refetchInvoice,
  } = useQuery<GetInvoiceByIdData, GetInvoiceByIdVars>(GET_INVOICE_BY_ID, {
    variables: { invoiceId },
    onError(err) {
      console.error("failed to load invoice", err);
    },
  });

  const {
    data: transactionData,
    loading: transactionLoading,
    error: transactionError,
    refetch: refetchTransaction,
  } = useQuery<GetTransactionDetailData, GetTransactionDetailVars>(GET_TRANSACTION_DETAIL, {
    variables: {
      transactionId,
    },
  });

  const [getTransactionImages, { data: imagesData, loading: imagesLoading, error: getImagesError }] = useMutation<
    GetTransactionImagesData,
    GetTransactionImagesVars
  >(GET_TRANSACTION_IMAGES, {
    onError(err) {
      console.error("failed to fetch transaction images", err);
    },
  });

  useEffect(() => {
    getTransactionImages({ variables: { transactionId } });
  }, [getTransactionImages, transactionId]);

  const error = getImagesError || transactionError || invoiceError;
  const loading = imagesLoading || transactionLoading || invoiceLoading;

  const slides: CarouselSlide[] = (
    [
      transactionData?.transaction?.sessionStartedAtLocal && imagesData?.transactionImages?.licensePlateImageUrl
        ? {
            title: "License Plate Capture",
            date: new Date(transactionData.transaction.sessionStartedAtLocal),
            imageSrc: imagesData.transactionImages.licensePlateImageUrl,
          }
        : null,
      transactionData?.transaction?.sessionStartedAtLocal && imagesData?.transactionImages?.sessionStartImageUrl
        ? {
            title: "Park Start",
            date: new Date(transactionData.transaction.sessionStartedAtLocal),
            imageSrc: imagesData.transactionImages.sessionStartImageUrl,
          }
        : null,
      transactionData?.transaction?.sessionEndedAtLocal && imagesData?.transactionImages?.sessionEndImageUrl
        ? {
            title: "Park End",
            date: new Date(transactionData.transaction.sessionEndedAtLocal),
            imageSrc: imagesData.transactionImages.sessionEndImageUrl,
          }
        : null,
    ] as (CarouselSlide | null)[]
  ).filter((v): v is CarouselSlide => v !== null);

  return (
    <Paper sx={{ p: 3 }}>
      {error ? (
        <LocalLevelError
          onTryAgain={() => {
            getTransactionImages({ variables: { transactionId } });
            refetchTransaction();
            refetchInvoice();
          }}
        />
      ) : (
        <Stack spacing={3}>
          <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
            <LoadingSkeleton loading={loading}>
              <Typography variant={"h5"} fontWeight={400}>
                Invoice Number: {`${invoiceData?.invoice?.index}`.padStart(6, "0")}
              </Typography>
            </LoadingSkeleton>
          </Box>
          <ImageCarousel loading={loading} slides={slides} />
        </Stack>
      )}
    </Paper>
  );
};

export interface InvoiceSessionImageCarouselProps {
  invoiceId: string;
  transactionId: string;
}

export default InvoiceSessionImageCarousel;
